import { IsHydrating, useMounted } from '@news-mono/web-common'
import React from 'react'

export interface LazyLoadingResult<T extends HTMLElement> {
    lazyLoadHasTriggered: boolean
    wasLazyLoaded: boolean
    lazyLoadRef: React.RefObject<T>
}

export function useLazyLoading<T extends HTMLElement>(): LazyLoadingResult<T> {
    const isHydrating = React.useContext(IsHydrating)
    const [isLoaded, setLoaded] = React.useState(false)
    const [wasLazyLoaded, setWasLazyLoaded] = React.useState(false)
    const isMounted = useMounted()
    const lazyLoadRef = React.useRef<T | null>(null)

    React.useEffect(() => {
        if (!lazyLoadRef.current) {
            console.debug(
                `innerRef passed by 'useLazyLoading' has not been added to an element`,
            )
            return
        }

        // Once loaded, we stay loaded
        if (isLoaded) return

        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) {
                // Schedule state change for after all components have determined if they are on screen
                setTimeout(() => {
                    // Only update state if we haven't been unmounted while waiting for the timeout
                    if (isMounted()) {
                        setLoaded(true)
                    }
                })
                observer.disconnect()
            }
        })

        observer.observe(lazyLoadRef.current)

        return () => {
            observer.disconnect()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoaded])

    React.useEffect(() => {
        // If not loaded initially, it was lazy loaded
        setWasLazyLoaded(!isLoaded)
    }, [isLoaded])

    return {
        lazyLoadHasTriggered: !isHydrating && isLoaded,
        wasLazyLoaded,
        lazyLoadRef,
    }
}
